/**
 * SpriteUI — Sprite-based UI factories using PixelLab pixel art assets.
 * Uses Phaser 3.80+ native NineSlice for scalable panels/buttons/bars.
 *
 * Mirrors the TechUI public API so callers can switch transparently
 * via UISwitch.ts (gated by GOD.useSpriteUI).
 */

import Phaser from 'phaser';
import { TECH, type TechButtonOptions } from '@/ui/TechUI';
import { UI_NINE_SLICE, UI_FIXED, type NineSliceConfig } from '@/ui/SpriteUIConfig';
import { SoundManager } from '@/systems/SoundManager';

/** Scale factor applied to all sprite UI elements (1.0 = same as TechUI size). */
const UI_SCALE = 1.4;

/** Hex number to CSS color string. */
function hexStr(c: number): string {
  return '#' + c.toString(16).padStart(6, '0');
}

// ── NineSlice helpers ──

/** Create a NineSlice from a config key, clamping to minimum dimensions. */
function makeNineSlice(
  scene: Phaser.Scene,
  configKey: string,
  x: number, y: number,
  w: number, h: number,
): Phaser.GameObjects.NineSlice {
  const cfg = UI_NINE_SLICE[configKey];
  if (!cfg) throw new Error(`SpriteUI: unknown NineSlice config '${configKey}'`);
  const minW = cfg.leftWidth + cfg.rightWidth + 2;
  const minH = cfg.topHeight + cfg.bottomHeight + 2;
  return scene.add.nineslice(
    x, y, cfg.texture, undefined,
    Math.max(minW, w), Math.max(minH, h),
    cfg.leftWidth, cfg.rightWidth, cfg.topHeight, cfg.bottomHeight,
  );
}

// ── Panel ──

export type SpritePanelType = 'large' | 'small' | 'wave' | 'upgrade' | 'frame';

/**
 * Create a sprite-based panel using NineSlice.
 * Replaces fillTechRect + drawTechRect combinations.
 */
export function createSpritePanel(
  scene: Phaser.Scene,
  x: number, y: number,
  w: number, h: number,
  panelType: SpritePanelType = 'large',
  depth = 1,
): Phaser.GameObjects.NineSlice {
  const key = panelType === 'frame' ? 'frame_square' : `panel_${panelType}`;
  return makeNineSlice(scene, key, x, y, w * UI_SCALE, h * UI_SCALE)
    .setOrigin(0.5)
    .setDepth(depth);
}

// ── Button ──

/**
 * Create a sprite-based button. Returns Container (same as createTechButton).
 * Reuses the TechButtonOptions interface for drop-in compatibility.
 */
export function createSpriteButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  opts: TechButtonOptions,
): Phaser.GameObjects.Container {
  const color = opts.color ?? TECH.COLOR;
  const fontSize = opts.fontSize ?? '14px';
  const fontStyle = opts.fontStyle ?? 'bold';
  const depth = opts.depth ?? 2;

  const container = scene.add.container(x, y).setDepth(depth);

  // Measure text for auto-sizing
  const textObj = scene.add.text(0, 0, label, {
    fontSize, fontFamily: 'monospace', fontStyle,
    color: hexStr(color),
  }).setOrigin(0.5);

  const padX = 24;
  const padY = 10;
  const btnW = opts.width ?? (textObj.width + padX * 2);
  const btnH = opts.height ?? Math.max(40, textObj.height + padY * 2);

  // Optional description (subtitle)
  let descObj: Phaser.GameObjects.Text | null = null;
  let totalH = btnH;
  if (opts.description) {
    descObj = scene.add.text(0, 12, opts.description, {
      fontSize: '11px', color: hexStr(TECH.DIM), fontFamily: 'monospace',
    }).setOrigin(0.5);
    totalH = Math.max(btnH, textObj.height + descObj.height + padY * 2 + 6);
    textObj.setY(-8);
    descObj.setY(10);
  }

  // Optional icon
  if (opts.icon) {
    const iconText = scene.add.text(-textObj.width / 2 - 14, textObj.y, opts.icon, {
      fontSize, fontFamily: 'monospace',
    }).setOrigin(0.5);
    container.add(iconText);
  }

  const finalW = opts.width ?? btnW;
  const finalH = opts.height ?? totalH;

  // NineSlice background sprite
  const ns = makeNineSlice(scene, 'button_wide', 0, 0, finalW, finalH)
    .setOrigin(0.5);

  // Hit area (transparent, on top)
  const hitArea = scene.add.rectangle(0, 0, finalW, finalH, 0x000000, 0)
    .setInteractive({ useHandCursor: true });

  container.add(ns);
  container.add(textObj);
  if (descObj) container.add(descObj);
  container.add(hitArea);

  // Apply sprite UI scale
  container.setScale(UI_SCALE);

  // ── Hover: tint brighten + scale ──
  const hoverScale = UI_SCALE * 1.05;
  const baseScale = UI_SCALE;
  const pressScale = UI_SCALE * 0.97;

  hitArea.on('pointerover', () => {
    ns.setTint(0xddddff);
    textObj.setColor('#ffffff');
    if (descObj) descObj.setColor(hexStr(color));
    scene.tweens.add({
      targets: container, scaleX: hoverScale, scaleY: hoverScale,
      duration: 120, ease: 'Sine.easeOut',
    });
  });
  hitArea.on('pointerout', () => {
    ns.clearTint();
    textObj.setColor(hexStr(color));
    if (descObj) descObj.setColor(hexStr(TECH.DIM));
    scene.tweens.add({
      targets: container, scaleX: baseScale, scaleY: baseScale,
      duration: 120, ease: 'Sine.easeOut',
    });
  });

  // ── Press: darken + shrink then spring back ──
  hitArea.on('pointerdown', () => {
    ns.setTint(0x888899);
    scene.tweens.add({
      targets: container, scaleX: pressScale, scaleY: pressScale,
      duration: 60, ease: 'Sine.easeOut',
      onComplete: () => {
        ns.setTint(0xddddff);
        scene.tweens.add({
          targets: container, scaleX: hoverScale, scaleY: hoverScale,
          duration: 60, ease: 'Sine.easeOut',
        });
      },
    });
    SoundManager.play('click');
    opts.onClick();
  });

  return container;
}

/**
 * Create a small inline sprite button (mirrors createSmallTechButton).
 */
export function createSmallSpriteButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  color: number,
  onClick: () => void,
  depth = 2,
): Phaser.GameObjects.Container {
  return createSpriteButton(scene, x, y, label, {
    onClick, color, fontSize: '12px', height: 32, depth,
  });
}

// ── Bar frame ──

export type SpriteBarType = 'hp' | 'currency' | 'frame';

/**
 * Create a sprite-based horizontal bar frame using NineSlice.
 * The bar fill is still a separate colored Rectangle drawn inside.
 */
export function createSpriteBar(
  scene: Phaser.Scene,
  x: number, y: number,
  w: number,
  barType: SpriteBarType = 'hp',
  depth = 1,
): Phaser.GameObjects.NineSlice {
  const key = barType === 'frame' ? 'bar_frame' : `bar_${barType}`;
  const cfg = UI_NINE_SLICE[key];
  return makeNineSlice(scene, key, x, y, w * UI_SCALE, cfg.nativeH)
    .setOrigin(0.5)
    .setDepth(depth);
}

// ── Banner ──

export type SpriteBannerType = 'title' | 'victory' | 'defeat';

/**
 * Create a fixed-size sprite banner (title, victory, defeat).
 * These are used as-is (not NineSlice) since they have unique art.
 */
export function createSpriteBanner(
  scene: Phaser.Scene,
  x: number, y: number,
  bannerType: SpriteBannerType,
  depth = 5,
): Phaser.GameObjects.Image {
  const key = UI_FIXED[`banner_${bannerType}` as keyof typeof UI_FIXED];
  return scene.add.image(x, y, key)
    .setOrigin(0.5)
    .setScale(UI_SCALE)
    .setDepth(depth);
}

// ── Slot ──

/**
 * Create a component slot image (locked or unlocked).
 */
export function createSpriteSlot(
  scene: Phaser.Scene,
  x: number, y: number,
  locked = false,
  depth = 1,
): Phaser.GameObjects.Image {
  const key = locked ? UI_FIXED.slot_locked : UI_FIXED.slot_component;
  return scene.add.image(x, y, key)
    .setOrigin(0.5)
    .setScale(UI_SCALE)
    .setDepth(depth);
}

// ── Icon frame ──

/**
 * Create a small circular icon frame.
 */
export function createSpriteIconFrame(
  scene: Phaser.Scene,
  x: number, y: number,
  depth = 1,
): Phaser.GameObjects.Image {
  return scene.add.image(x, y, UI_FIXED.icon_frame)
    .setOrigin(0.5)
    .setScale(UI_SCALE)
    .setDepth(depth);
}
